// Counting Hands — seeded random puzzle generator (declarer view). Emits
// objects in the same compass puzzle shape the shared CountingTrumpsTrainer
// already consumes (seatMode "compass", rounds/plays with showOut flags,
// DISTRIBUTION_GUESS prompts), so the trainer UI is unchanged.
//
// Modes: 1, 2 or 3 counted suits. Suit 1 is always the trump suit (declarer
// draws trumps); modes 2/3 then run one or two side suits, each with its own
// distribution question. Trumps are drawn to exhaustion in modes 2/3 so a
// defender can never ruff the side-suit run.
//
// Everything derives from (seed, mode): seed -> deal -> play script -> prompts.
// A bug report is a seed number plus a mode.
//
// WHEN THE QUESTION IS ASKED: as soon as the split is *knowable*, which is
// either (a) a defender shows out, or (b) the defenders' cards are exhausted
// with both still following — e.g. 5-4 fit, opponents 2-2: after two rounds
// there is nothing left to ask about, so we ask then and do not play a third
// round. (Paul, 2026-08-28.)
//
// NOTE ON WORDING: prompt/label text lives in the TEMPLATE STRINGS block below
// so Paul can reword it in one place. Structural placeholders only — final
// wording is his.

const RANKS = ["A", "K", "Q", "J", "T", "9", "8", "7", "6", "5", "4", "3", "2"];
const RANK_ORDER = new Map(RANKS.map((r, i) => [r, i]));
const SUITS = ["S", "H", "D", "C"];
const SUIT_SYMBOL = { S: "♠", H: "♥", D: "♦", C: "♣" };
const GAME_CONTRACT = { S: "4♠", H: "4♥", D: "5♦", C: "5♣" };

const SEATS = ["north", "east", "south", "west"];
const SEAT_LETTER = { north: "N", east: "E", south: "S", west: "W" };
const SEAT_WORD = { north: "North", east: "East", south: "South", west: "West" };
const CLOCKWISE = ["north", "east", "south", "west"]; // matches src/bridge/seatCompassMaps
const DEFENDERS = ["west", "east"];

/** Supported counted-suit counts. */
export const COUNTING_HANDS_MODES = [1, 2, 3];
export const DEFAULT_COUNTING_HANDS_MODE = 1;

// ---- TEMPLATE STRINGS (Paul: edit wording here) ----------------------------
export const T = {
  title: (seed) => `Deal ${seed}`,
  contractLabel: (suit) => `Contract is ${GAME_CONTRACT[suit]} by South`,
  trickLabel: (n) => `Trick ${n}`,
  trickLabelShowOut: (n, who) => `Trick ${n} (${who} shows out)`,
  // Asked when a defender failed to follow.
  distributionPrompt: (suit) =>
    `A defender has shown out. How many ${SUIT_SYMBOL[suit]} did each player start with?`,
  // Asked when nobody showed out but the suit ran out anyway (e.g. 2-2).
  distributionPromptExhausted: (suit) =>
    `That is the last of them. How many ${SUIT_SYMBOL[suit]} did each player start with?`,
  distributionSuccess: "Right.",
  // Asked once, after the last counted suit, for each defender in turn. Paul's
  // wording (2026-08-31): "ask what each players suit distribution is, spades
  // hearts diamonds clubs".
  seatShapePrompt: (seatWord) =>
    `What was ${seatWord}'s suit distribution? (spades hearts diamonds clubs)`,
  // Real deals from records (realCountHands.js, 9 Oct 2026) — structural labels only.
  // Title = event + board; the contract line also carries the result at the table.
  titleReal: (event, board) => `${event} · Board ${board}`,
  // The engine does not show `title` in this layout, so the event and board ride on the contract pill.
  contractLabelReal: (contract, declarerName, tricksMade, claimed, event, board) => {
    const where = event ? `${event}, board ${board} · ` : "";
    const made = Number.isFinite(tricksMade) ? ` · ${tricksMade} tricks made${claimed ? " (claimed)" : ""}` : "";
    return `${where}${contract} by ${declarerName}${made}`;
  },
  loadingReal: "Loading deals…",
  // Shown once above the table on real deals (Paul, 9 Oct 2026: "make a note that auctions can be a bit artificial").
  auctionNoteReal: "Each deal is rotated so you sit South. The auctions are as bid at the table and can be a bit artificial.",
  // Mode picker — neutral structural labels only.
  modeLabel: (n) => (n === 1 ? "1 suit" : `${n} suits`),
  // Membership gate wording. Structural/commercial copy, not bridge content —
  // reword freely. Level 1 is open to everyone; 2 and 3 need a subscription.
  modeLockedNote: "Levels 2 and up are for members. A 7-day free trial opens them all.",
  modeLockedRedirected: "Levels 2 and up are for members, so you're on level 1. A 7-day free trial opens them all.",
  modeUpgradeCta: "Start free trial",
};
// ----------------------------------------------------------------------------

// Pre-fill the counts the viewer can simply read off the table (their own hand
// and dummy). The plan floated shrinking this on later suits to raise
// difficulty, but a visible hand is free information — hiding it adds typing,
// not counting. Flip to false to ask for all four seats every time.
const PREFILL_VISIBLE_HANDS = true;

// Deterministic RNG (mulberry32) so every deal replays from its seed.
function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const pick = (rng, arr) => arr[Math.floor(rng() * arr.length)];

function shuffled(rng, arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const sortDesc = (ranks) => ranks.slice().sort((x, y) => RANK_ORDER.get(x) - RANK_ORDER.get(y));
const higher = (a, b) => (RANK_ORDER.get(a) < RANK_ORDER.get(b) ? a : b);

function choose(n, k) {
  if (k < 0 || k > n) return 0;
  let out = 1;
  for (let i = 0; i < k; i++) out = (out * (n - i)) / (i + 1);
  return out;
}

/**
 * A-priori odds of `rest` missing cards splitting with `west` of them on the
 * left, by vacant spaces (13 unknown cards each at trick one). Drawing splits
 * with these weights keeps the deals looking like real bridge — a 4-0 turns up
 * about once in ten, not one time in five.
 */
function aPrioriSplitWeight(rest, west) {
  return choose(rest, west) * choose(26 - rest, 13 - west);
}

function pickWeighted(rng, items, weights) {
  const total = weights.reduce((a, b) => a + b, 0);
  if (!(total > 0)) return pick(rng, items);
  let r = rng() * total;
  for (let i = 0; i < items.length; i++) {
    r -= weights[i];
    if (r <= 0) return items[i];
  }
  return items[items.length - 1];
}

function followOrderFrom(leader) {
  const i = CLOCKWISE.indexOf(leader);
  return [0, 1, 2, 3].map((k) => CLOCKWISE[(i + k) % 4]);
}

/**
 * The round (1-based) after which the defenders' split is fully determined:
 * either the short defender has failed to follow, or every outstanding card
 * has appeared. Whichever comes first.
 */
function determinationRound(west, east) {
  const rest = west + east;
  return Math.min(Math.min(west, east) + 1, Math.ceil(rest / 2));
}

/**
 * Length plan for one counted suit. `runRounds` is how many rounds of the suit
 * get played: normally we stop the moment the split is known, but the trump
 * suit in modes 2/3 is drawn until both defenders are out of trumps, so the
 * side-suit run that follows cannot be ruffed.
 */
function planCountedSuit(rng, { isTrump, mode, capacity }) {
  const declOpts = (isTrump ? [5, 6] : [3, 4, 5]).filter((n) => n <= capacity.south);
  const dummyOpts = (isTrump ? [3, 4, 5] : [3, 4]).filter((n) => n <= capacity.north);
  if (!declOpts.length || !dummyOpts.length) return null;

  const declLen = pick(rng, declOpts);
  const dummyLen = pick(rng, dummyOpts);
  const rest = 13 - declLen - dummyLen;
  if (rest < 3 || rest > 7) return null;

  const maxRounds = Math.min(declLen, dummyLen);
  const drainDefenders = isTrump && mode > 1;

  const options = [];
  const weights = [];
  for (let west = 0; west <= rest; west++) {
    const east = rest - west;
    if (west > capacity.west || east > capacity.east) continue;
    const det = determinationRound(west, east);
    const runRounds = drainDefenders ? Math.max(det, west, east) : det;
    if (runRounds > maxRounds) continue;
    options.push({ west, east, det, runRounds });
    weights.push(aPrioriSplitWeight(rest, west));
  }
  if (!options.length) return null;

  const chosen = pickWeighted(rng, options, weights);
  return { declLen, dummyLen, ...chosen };
}

/**
 * Deal the ranks of one counted suit. Declarer's side gets the top `runRounds`
 * cards between them (split at random between hand and dummy) so declarer wins
 * every round of the run and the lead never leaves the partnership; the rest of
 * the suit is random.
 */
function dealCountedSuitRanks(rng, plan) {
  const top = RANKS.slice(0, plan.runRounds);
  const rest = shuffled(rng, RANKS.slice(plan.runRounds));
  const out = { south: [], north: [], west: [], east: [] };
  for (const r of top) {
    const canS = out.south.length < plan.declLen;
    const canN = out.north.length < plan.dummyLen;
    if (canS && canN) (rng() < 0.5 ? out.south : out.north).push(r);
    else if (canS) out.south.push(r);
    else if (canN) out.north.push(r);
    else return null;
  }
  const fill = (seat, len) => { while (out[seat].length < len) out[seat].push(rest.pop()); };
  fill("south", plan.declLen);
  fill("north", plan.dummyLen);
  fill("west", plan.west);
  fill("east", plan.east);
  return out;
}

/** Lowest card a defender can spare, preferring suits nobody has to count yet. */
function chooseDiscard(remaining, seat, ledSuit, countedSuits, askedSuits) {
  const uncounted = SUITS.filter((s) => s !== ledSuit && !countedSuits.includes(s));
  const asked = countedSuits.filter((s) => s !== ledSuit && askedSuits.has(s));
  const future = countedSuits.filter((s) => s !== ledSuit && !askedSuits.has(s));
  for (const group of [uncounted, asked, future]) {
    const withCards = group
      .filter((s) => remaining[seat][s].length)
      .sort((a, b) => remaining[seat][b].length - remaining[seat][a].length);
    if (withCards.length) {
      const s = withCards[0];
      return { rank: remaining[seat][s].pop(), suit: s };
    }
  }
  return null;
}

/**
 * Run the template line: for each counted suit in turn, declarer's side cashes
 * it from the top, defenders follow low and discard once void. Returns the
 * rounds plus, for each counted suit, the round index the question fires on.
 */
function scriptPlay(hands, countedSuits, plans) {
  const remaining = {};
  for (const seat of SEATS) {
    remaining[seat] = {};
    for (const s of SUITS) remaining[seat][s] = hands[seat][s].slice();
  }

  const rounds = [];
  const asks = [];
  const askedSuits = new Set();
  let leader = "south";

  for (let i = 0; i < countedSuits.length; i++) {
    const suit = countedSuits[i];
    const plan = plans[i];
    const shownOut = { west: false, east: false };
    let askRoundIdx = -1;
    let askAfterShowOut = false;

    for (let r = 0; r < plan.runRounds; r++) {
      if (!remaining.south[suit].length || !remaining.north[suit].length) return null;

      // Declarer's side must hold the highest card left in the suit, or it
      // loses the lead and the template breaks.
      const sHi = remaining.south[suit][0];
      const nHi = remaining.north[suit][0];
      const declHi = higher(sHi, nHi);
      const holder = declHi === sHi ? "south" : "north";
      const defCards = [...remaining.west[suit], ...remaining.east[suit]];
      if (defCards.some((c) => RANK_ORDER.get(c) < RANK_ORDER.get(declHi))) return null;

      const plays = [];
      let roundShowOuts = [];
      for (const seat of followOrderFrom(leader)) {
        if (seat === holder) {
          plays.push({ seat: SEAT_LETTER[seat], card: { rank: remaining[seat][suit].shift(), suit } });
        } else if (remaining[seat][suit].length) {
          plays.push({ seat: SEAT_LETTER[seat], card: { rank: remaining[seat][suit].pop(), suit } });
        } else {
          if (!DEFENDERS.includes(seat)) return null; // dummy/declarer must always follow
          const card = chooseDiscard(remaining, seat, suit, countedSuits, askedSuits);
          if (!card) return null;
          // Only the FIRST failure to follow is a show-out; later discards in
          // the same suit are just discards (and must not re-trigger a pause).
          const first = !shownOut[seat];
          plays.push({ seat: SEAT_LETTER[seat], card, ...(first ? { showOut: true } : {}) });
          if (first) { shownOut[seat] = true; roundShowOuts.push(seat); }
        }
      }

      const trickNo = rounds.length + 1;
      rounds.push({
        label: roundShowOuts.length
          ? T.trickLabelShowOut(trickNo, roundShowOuts.map((s) => SEAT_WORD[s]).join(" and "))
          : T.trickLabel(trickNo),
        plays,
      });
      leader = holder;

      if (askRoundIdx < 0 && r + 1 >= plan.det) {
        askRoundIdx = rounds.length - 1;
        askAfterShowOut = roundShowOuts.length > 0;
      }
    }

    if (askRoundIdx < 0) return null;
    askedSuits.add(suit);
    asks.push({ suit, atRoundIdx: askRoundIdx, afterShowOut: askAfterShowOut, plan });
  }

  return { rounds, asks };
}

function tryBuildPuzzle(rng, seed, mode) {
  const suitOrder = shuffled(rng, SUITS);
  const countedSuits = suitOrder.slice(0, mode);
  const trumpSuit = countedSuits[0];

  // Plan the counted suits, keeping each seat inside 13 cards and leaving the
  // defenders something to discard once they are void.
  const capacity = { north: 13, east: 13, south: 13, west: 13 };
  const plans = [];
  for (let i = 0; i < countedSuits.length; i++) {
    const reserve = i === countedSuits.length - 1 ? 0 : 1; // room for later discards
    const plan = planCountedSuit(rng, {
      isTrump: i === 0,
      mode,
      capacity: {
        south: capacity.south,
        north: capacity.north,
        west: Math.max(0, capacity.west - reserve),
        east: Math.max(0, capacity.east - reserve),
      },
    });
    if (!plan) return null;
    capacity.south -= plan.declLen;
    capacity.north -= plan.dummyLen;
    capacity.west -= plan.west;
    capacity.east -= plan.east;
    plans.push(plan);
  }
  if (DEFENDERS.some((s) => capacity[s] < 1)) return null;

  // Deal: counted suits by construction, everything else shuffled in.
  const hands = {};
  for (const seat of SEATS) hands[seat] = { S: [], H: [], D: [], C: [] };
  for (let i = 0; i < countedSuits.length; i++) {
    const ranks = dealCountedSuitRanks(rng, plans[i]);
    if (!ranks) return null;
    for (const seat of SEATS) hands[seat][countedSuits[i]] = sortDesc(ranks[seat]);
  }
  const pool = [];
  for (const s of SUITS) {
    if (countedSuits.includes(s)) continue;
    for (const r of RANKS) pool.push({ rank: r, suit: s });
  }
  const deck = shuffled(rng, pool);
  for (const seat of SEATS) {
    let need = 13 - SUITS.reduce((n, s) => n + hands[seat][s].length, 0);
    while (need-- > 0) {
      const c = deck.pop();
      if (!c) return null;
      hands[seat][c.suit].push(c.rank);
    }
    for (const s of SUITS) hands[seat][s] = sortDesc(hands[seat][s]);
  }
  // Freak shapes read as a puzzle-generator artefact rather than a deal, and
  // the opponents would have bid them. Deal again instead.
  for (const seat of SEATS) {
    if (SUITS.some((s) => hands[seat][s].length >= 8)) return null;
  }

  const script = scriptPlay(hands, countedSuits, plans);
  if (!script) return null;
  if (script.rounds.length > 12) return null;

  const handString = (seat) => ({
    S: hands[seat].S.join(""),
    H: hands[seat].H.join(""),
    D: hands[seat].D.join(""),
    C: hands[seat].C.join(""),
  });

  const customPrompts = script.asks.map((ask, i) => ({
    id: `ch-${seed}-m${mode}-dist${i + 1}`,
    type: "DISTRIBUTION_GUESS",
    suit: ask.suit,
    atRoundIdx: ask.atRoundIdx,
    promptText: ask.afterShowOut
      ? T.distributionPrompt(ask.suit)
      : T.distributionPromptExhausted(ask.suit),
    expectedDistribution: {
      north: ask.plan.dummyLen,
      east: ask.plan.east,
      south: ask.plan.declLen,
      west: ask.plan.west,
    },
    // The viewer can see their own hand and dummy — those counts come
    // pre-filled; the two defenders are what they must work out.
    ...(PREFILL_VISIBLE_HANDS ? { fixed: { south: ask.plan.declLen, north: ask.plan.dummyLen } } : {}),
    successText: T.distributionSuccess,
  }));

  // CAPSTONE (mode 3): once all three suits are counted, the fourth falls out by
  // subtraction, so ask each defender's whole shape. Only the DEFENDERS are
  // asked - declarer and dummy are face up and reading a visible hand is not
  // counting (same reasoning as PREFILL_VISIBLE_HANDS above). The trainer works
  // the answer out from `shownHands`, so these carry no expected value.
  const lastAsk = script.asks[script.asks.length - 1];
  if (mode === 3 && lastAsk) {
    DEFENDERS.forEach((seat, i) => {
      customPrompts.push({
        id: `ch-${seed}-m${mode}-shape${i + 1}`,
        type: "SEAT_SHAPE",
        seat,
        atRoundIdx: lastAsk.atRoundIdx,
        promptText: T.seatShapePrompt(SEAT_WORD[seat]),
        successText: T.distributionSuccess,
      });
    });
  }

  return {
    id: `ch-${seed}-m${mode}`,
    seed,
    mode,
    countedSuits,
    difficulty: mode,
    seatMode: "compass",
    playEngine: "compassClockwise",
    trainerEngine: "compass",
    title: T.title(seed),
    trumpSuit,
    contract: GAME_CONTRACT[trumpSuit],
    dealerCompass: "S",
    declarerCompass: "S",
    viewerCompass: "S",
    visibleFullHandSeats: ["south", "north"],
    revealFullHandsAtEnd: ["east", "west"],
    promptOptions: {
      promptPlacement: "left",
      hideAuction: true,
      contractLabel: T.contractLabel(trumpSuit),
      disableWarmupTrumpGuess: true,
      questionNumbers: [],
      customPrompts,
    },
    shownHands: {
      north: handString("north"),
      east: handString("east"),
      south: handString("south"),
      west: handString("west"),
    },
    rounds: script.rounds,
  };
}

export function normalizeMode(value) {
  const n = Number(value);
  return COUNTING_HANDS_MODES.includes(n) ? n : DEFAULT_COUNTING_HANDS_MODE;
}

/**
 * One puzzle for (seed, mode). The constraints interact, so a seed may need a
 * few attempts before it yields a legal deal — the attempts run off the same
 * seeded stream, so the result is still fully deterministic.
 */
export function generateCountingHandsPuzzle(seed, mode = DEFAULT_COUNTING_HANDS_MODE) {
  const m = normalizeMode(mode);
  const rng = mulberry32(seed * 4 + m);
  for (let attempt = 0; attempt < 400; attempt++) {
    const puzzle = tryBuildPuzzle(rng, seed, m);
    if (puzzle) return puzzle;
  }
  throw new Error(`countingHandsGenerator: seed ${seed} produced no legal deal for mode ${m}`);
}

/** A batch of distinct seeded puzzles (fresh every visit unless seeds given). */
export function generateCountingHandsPuzzles(count = 16, seeds = null, mode = DEFAULT_COUNTING_HANDS_MODE) {
  const m = normalizeMode(mode);
  const list = [];
  const used = new Set();
  for (let i = 0; i < count; i++) {
    let seed = seeds ? seeds[i] : Math.floor(Math.random() * 900000) + 100000;
    while (seed == null || used.has(seed)) seed = Math.floor(Math.random() * 900000) + 100000;
    used.add(seed);
    list.push(generateCountingHandsPuzzle(seed, m));
  }
  return list;
}
